// web/src/components/ds/IconFamilyLucide.tsx — DS.5 (sprint aos-ds-shell, spec §2 D6).
//
// The one icon-family provider every DS piece draws through. The vendored
// `Icon` primitive's DEFAULT renderer emits a Material Symbols ligature span;
// this app never loads that font (Lucide is the decision, DS.4 D3g), so
// without a provider a DS piece renders the literal word.
//
// Resolution is two hops because the two key spaces are disjoint (spec §0.11b):
//   Material snake_case  --ds-icon-aliases.ts-->  Lucide PascalCase
//   Lucide PascalCase    --atoms/Icon.tsx iconMap-->  component
// `iconMap` is the app's single icon registry (42 importers, DS-C6): this file
// is a second CONSUMER of it, never a second registry.
//
// An unmapped name THROWS (D6, Error Response Hierarchy level 4): `KpiCard`'s
// `icon` prop and `RichTextEditor`'s toolbar array carry consumer-supplied
// names no grep can see, and a silent fallback (null, or the Material span)
// is exactly the invisible-icon class this provider exists to kill.

import { createElement, type ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { IconFamilyProvider, type IconRenderer } from '@ds/primitives/icon-family';
import { iconMap } from '../atoms/Icon';
import { DS_ICON_ALIASES } from './ds-icon-aliases';

/** Material name → Lucide component, or throw (drill e2). Exported for tests. */
export function resolveDsIcon(name: string): LucideIcon {
  const lucideName = DS_ICON_ALIASES[name];
  if (!lucideName) {
    throw new Error(
      `IconFamilyLucide: DS icon "${name}" has no Material→Lucide alias in ` +
        'web/src/components/ds/ds-icon-aliases.ts (spec aos-ds-shell D6: unmapped ⇒ throw, ' +
        'never a Material fallback)',
    );
  }
  return iconMap[lucideName];
}

const renderLucide: IconRenderer = (name, { px, className, style }) =>
  createElement(resolveDsIcon(name), {
    size: px,
    className,
    style,
    'aria-hidden': true,
  });

export function IconFamilyLucide({ children }: { children: ReactNode }) {
  return <IconFamilyProvider render={renderLucide}>{children}</IconFamilyProvider>;
}

export default IconFamilyLucide;
